<template>
  <div>
    <p
      v-if="computedEventDates"
      :class="$style.eventDate"
      v-html="computedEventDates"
    />

    <template v-if="computedDateDetails">
      <ProgressBar
        v-if="computedDateDetails.progressPercentage < 100"
        :class="$style.progressBar"
        :percentage="computedDateDetails.progressPercentage"
      />
      <p v-else :class="$style.dateDistance">{{ computedEventHasEnded }}</p>

      <!-- event in the future -->
      <p
        v-if="computedDateDetails.startDateDistanceFromCurrentDay > 0"
        :class="$style.dateDistance"
        v-html="computedStartsIn"
      />

      <!-- event has started -->
      <p
        v-else-if="computedDateDetails.endDateDistanceFromCurrentDay > 0"
        :class="$style.dateDistance"
        v-html="computedEndsIn"
      />

      <p
        v-else-if="computedDateDetails.endDateDistanceFromCurrentDay === 0"
        :class="$style.dateDistance"
        v-html="computedEndsToday"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import getDateDetails from '../utils/getDateDetails'
import { formatLocalizedDate } from '../utils/localizeDates'

const { localizedStrings } = useLocalizedStrings()
const { locale } = useI18n()

const props = defineProps<{
  startDatetime: string
  endDatetime: string
}>()

const computedDateDetails = computed(() => {
  return getDateDetails(props.startDatetime, props.endDatetime)
})
const computedEventHasEnded = computed(() => {
  return localizedStrings.value['event-has-ended']
})
const computedEventDates = computed(() => {
  return `<b>${localizedStrings.value['event-dates']}:</b>
  ${formatLocalizedDate(
    computedDateDetails?.value?.formatedStartDate || '',
    'P',
    locale.value,
  )} -
  ${formatLocalizedDate(
    computedDateDetails?.value?.formatedEndDate || '',
    'P',
    locale.value,
  )}`
})
const computedStartsIn = computed(() => {
  return `${localizedStrings.value['starts-in'].replace(
    '{days}',
    String(computedDateDetails?.value?.startDateDistanceFromCurrentDay),
  )}`
})
const computedEndsIn = computed(() => {
  return `${localizedStrings.value['ends-in'].replace(
    '{days}',
    String(computedDateDetails?.value?.endDateDistanceFromCurrentDay),
  )}`
})
const computedEndsToday = computed(() => {
  return `${localizedStrings.value['ends']} <b>${localizedStrings.value['today']}</b>`
})
</script>

<style lang="scss" module>
.container {
  cursor: pointer;
  background: #fff;
  border: 1px solid var(--gray-4);
  box-shadow: 0 4px 13px rgba(#000, 0.06);
  display: grid;
  gap: 10px;
  text-align: center;
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;
  min-height: 134px;
  @include fluid-type(21px, 23px);

  @include media('xs') {
    grid-template-columns: 177px 1fr;
    gap: 20px;
    text-align: left;
  }

  @include media('lg') {
    gap: 30px;
  }

  &:hover,
  &:focus {
    border: 1px solid var(--gray-5);
    box-shadow: 0 4px 13px rgba(#000, 0.12);
  }
}

.content {
  padding: 16px;
}

.img {
  width: 100%;
  vertical-align: middle;

  @include media('xs') {
    height: 100%;
    object-fit: cover;
    width: auto;
  }
}

.title {
  font-family: var(--roboto-condensed);
  font-size: 25px;
  line-height: math.div(30, 25);
  margin-bottom: 3px;
}

.titleLink {
  color: inherit;
  text-decoration: none;
}

.eventDate {
  font-size: to-rem(17px);
  line-height: math.div(27px, 17px);
}

.progressBar {
  --progress-bar-background-color: #{rgba(#000, 0.11)};

  max-width: 170px;
  margin: 14px auto 5px;

  @include media('xs') {
    margin-left: 0;
    margin-right: 0;
  }
}

.dateDistance {
  font-size: to-rem(14px);
  line-height: math.div(28px, 14px);
  font-style: italic;
  font-family: var(--georgia);
}

.cta {
  margin-top: 17px;
}
</style>
